// src/components/promos/CustomerAllowlistPicker.tsx
import { useState } from "react";
import Input from "@/components/shared/ui/Input";
import { usePromoCustomerSearch } from "@/api/features/promos";
import type { PromoCustomerLite } from "@/api/features/promos";

type Props = {
  value: PromoCustomerLite[];
  onChange: (next: PromoCustomerLite[]) => void;
};

/**
 * Search + multi-select of customers for a restricted promo's allowlist. Emits
 * the selected customers (parent maps to `allowedUserIds`). Search is scoped to
 * the promos capability, so a promos admin doesn't need customers permission.
 */
export default function CustomerAllowlistPicker({ value, onChange }: Props) {
  const [q, setQ] = useState("");
  const { data: results, isFetching } = usePromoCustomerSearch(q);
  const selectedIds = new Set(value.map((v) => v.userId));

  const add = (c: PromoCustomerLite) => {
    if (selectedIds.has(c.userId)) return;
    onChange([...value, c]);
    setQ("");
  };
  const remove = (userId: number) => onChange(value.filter((v) => v.userId !== userId));

  const suggestions = (results ?? []).filter((r) => !selectedIds.has(r.userId));

  return (
    <div className="space-y-2">
      {value.length ? (
        <div className="flex flex-wrap gap-2">
          {value.map((c) => (
            <span
              key={c.userId}
              className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs text-sky-800"
            >
              {c.name || c.email || `#${c.userId}`}
              <button
                type="button"
                onClick={() => remove(c.userId)}
                className="text-sky-500 hover:text-sky-700"
                aria-label={`Remove ${c.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className="relative">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search customers by name or email…"
        />
        {q.trim().length >= 1 ? (
          <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
            {isFetching ? <div className="px-3 py-2 text-xs text-gray-400">Searching…</div> : null}
            {suggestions.map((r) => (
              <button
                key={r.userId}
                type="button"
                onClick={() => add(r)}
                className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-gray-50"
              >
                <span className="text-sm text-gray-900">{r.name || `#${r.userId}`}</span>
                {r.email ? <span className="text-xs text-gray-400">{r.email}</span> : null}
              </button>
            ))}
            {!isFetching && suggestions.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
            ) : null}
          </div>
        ) : null}
      </div>

      <p className="text-[11px] text-gray-400">
        {value.length
          ? `${value.length} customer${value.length > 1 ? "s" : ""} can redeem this promo.`
          : "Only the customers you add here can redeem this promo's code."}
      </p>
    </div>
  );
}
